/**
 * "Duplicate to edit" for built-in (code-defined) types. Every built-in has
 * a full data twin (builtins/index.ts: same drawing, params and behaviour);
 * a type without one falls back to a snapshot of its default glyph as
 * editable shapes (only the drawing carries over).
 */

import { OBJECT_DEFINITION_SCHEMA_VERSION, type ObjectDefinitionSpec, type ObjectShape, type Vec2 } from 'shared-types';
import type { RegisteredSketchObjectType } from '../registry';
import { BUILTIN_OBJECT_SPECS } from './builtins';
import { SketchObjectAnchorKind } from '../types';

const JOIN_EPS = 1e-6;
/** Heights of the built-ins aren't modelled (2D only); a neutral default. */
const DEFAULT_HEIGHT = 0.9;

const same = (a: Vec2, b: Vec2): boolean => Math.abs(a[0] - b[0]) < JOIN_EPS && Math.abs(a[1] - b[1]) < JOIN_EPS;

/** Chain flat `[x1,y1,z,x2,y2,z,…]` segments into polylines (consecutive shared endpoints joined). */
export function segmentsToPolylines(segments: readonly number[]): Vec2[][] {
  const lines: Vec2[][] = [];
  let current: Vec2[] | null = null;
  for (let i = 0; i + 5 < segments.length; i += 6) {
    const a: Vec2 = [segments[i], segments[i + 1]];
    const b: Vec2 = [segments[i + 3], segments[i + 4]];
    if (current && same(current[current.length - 1], a)) {
      current.push(b);
    } else {
      current = [a, b];
      lines.push(current);
    }
  }
  return lines;
}

const round = (v: number): number => Math.round(v * 1e5) / 1e5;

/** Editable spec of a built-in: its full data twin, else its default glyph at its default size. */
export function builtinTypeToSpec(type: RegisteredSketchObjectType): ObjectDefinitionSpec {
  const twin = BUILTIN_OBJECT_SPECS[type.id];
  if (twin) return twin;
  const { width, depth } = type.defaultSize;
  const segments = type.buildDefaultGlyph({ center: { x: 0, y: 0 }, dir: { x: 1, y: 0 }, norm: { x: 0, y: 1 }, width, depth, z: 0 });
  const shapes: ObjectShape[] = segmentsToPolylines(segments).map((points, i) => {
    const closed = points.length > 3 && same(points[0], points[points.length - 1]);
    return {
      id: `s${i + 1}`,
      kind: 'polyline',
      points: (closed ? points.slice(0, -1) : points).map(([u, n]): Vec2 => [round(u), round(n)]),
      closed,
    };
  });
  const isLine = type.anchor === SketchObjectAnchorKind.LINE;
  return {
    schemaVersion: OBJECT_DEFINITION_SCHEMA_VERSION,
    anchor: isLine ? 'LINE' : 'POINT',
    constraint: type.constraint,
    ...(type.drawLikeWall ? { drawLikeWall: true } : {}),
    defaultSize: { width, depth, height: DEFAULT_HEIGHT },
    params: [],
    shapes,
    groups: [],
    ...(isLine ? { handles: { start: [-0.5, 0], end: [0.5, 0] } } : {}),
    model3d: null,
  };
}
